import React, { useEffect, useState } from "react";
import { apiFetch } from "../api/client";
import AddCategory from "../components/AddCategory";
import { can, Permissions } from "../auth/authorization";
import { useNotification } from "../components/Notifications";

function ManageCategory() {
  const [showModal, setShowModal] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const notify = useNotification();

  useEffect(() => {
    setLoading(true);
    fetchData().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const res = await apiFetch(`/api/category`);
      if (!res.ok) return;
      const data = await res.json();
      const arr = Array.isArray(data) ? data : (data?.data || data?.Data || []);
      setCategories(arr || []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = (cat) => {
    setSelectedCategory(cat);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      if (!selectedCategory) return;
      const id = selectedCategory.categoryId || selectedCategory.id;
      const res = await apiFetch(`/api/category/${id}`, { method: "DELETE" });
      if (!res.ok) {
        const parsed = await res.json().catch(() => null);
        notify("error", { title: "Delete failed", text: parsed?.message || "Could not delete category." });
        setShowDeleteModal(false);
        return;
      }
      setCategories((prev) => prev.filter((c) => (c.categoryId || c.id) !== id));
      setShowDeleteModal(false);
      notify("success", { title: "Deleted", text: "Category removed." });
    } catch (err) {
      console.error(err);
      notify("error", { title: "Error", text: "Could not delete category." });
    }
  };

  return (
    <div className="col-span-12 lg:col-span-10 flex justify-center">
      <div className="flex flex-col gap-5 w-11/12 border-2 bg-gray-50 p-4 rounded-lg">
          <div className="flex justify-between items-center">
          <span className="font-bold text-lg text-gray-800">Manage Categories</span>
          {can(Permissions.Add) && (
            <button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 text-sm rounded-md" onClick={() => { setSelectedCategory(null); setShowModal(true); }}>+ Add Category</button>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 bg-white rounded-lg shadow">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {categories.map((c, idx) => (
                <tr key={c.categoryId || c.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{c.categoryId || c.id}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{c.categoryName || c.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex gap-2">
                      {can(Permissions.Edit) && (
                        <button onClick={() => { setSelectedCategory(c); setShowModal(true); }} className="px-3 py-1 bg-yellow-500 text-white text-sm rounded hover:bg-yellow-600">Edit</button>
                      )}
                      {can(Permissions.Delete) && (
                        <button onClick={() => handleDelete(c)} className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700">Delete</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && <AddCategory initial={selectedCategory} onAdded={fetchData} onClose={() => setShowModal(false)} />}

      {/* Delete confirmation modal */}
      {showDeleteModal && selectedCategory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg w-full max-w-sm">
            <h2 className="text-lg font-semibold mb-4 text-red-600">Confirm Delete</h2>
            <p>Are you sure you want to delete <strong>{selectedCategory.categoryName || selectedCategory.name}</strong>?</p>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowDeleteModal(false)} className="bg-gray-300 px-3 py-1 rounded">Cancel</button>
              <button onClick={confirmDelete} className="bg-red-600 text-white px-3 py-1 rounded">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageCategory;
